/**
 * OmegaSidebar — categorized side navigation for the OMEGA section.
 *
 * Desktop: fixed left column (224 px). Mobile: hamburger off-canvas drawer.
 * Kill Switch is pinned in the header — always visible, never scrolls away.
 * Settings is pinned at the bottom of the sidebar.
 *
 * Also exports OmegaLayoutShell — a pathless React Router layout route that
 * renders this sidebar + <Outlet /> so every /omega/* page gets the sidebar
 * automatically without each page importing it individually.
 */

import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

export interface OmegaNavItem {
  label: string;
  to: string;
}

export interface OmegaNavCategory {
  title: string;
  items: OmegaNavItem[];
}

/** The OMEGA navigation, by category. Exported pure for unit tests. */
export const OMEGA_NAV: OmegaNavCategory[] = [
  {
    title: 'Intelligence',
    items: [
      { label: 'Learning',        to: '/omega/learning' },
      { label: 'DLM',             to: '/omega/dlm' },
      { label: 'Research Teams',  to: '/omega/research/teams' },
      { label: 'Discovery',       to: '/omega/brain/discovery' },
      { label: 'Decomposition',   to: '/omega/decomposition' },
      { label: 'Knowledge Gates', to: '/omega/learning/gates' },
    ],
  },
  {
    title: 'Business',
    items: [
      { label: 'Outreach',        to: '/omega/outreach' },
      { label: 'Sales',           to: '/omega/sales' },
      { label: 'Products',        to: '/omega/products' },
      { label: 'Marketing',       to: '/omega/marketing' },
      { label: 'Customers',       to: '/omega/customers' },
      { label: 'Finance / Stripe', to: '/omega/finance' },
    ],
  },
  {
    title: 'Infrastructure',
    items: [
      { label: 'Loop',        to: '/omega/loop' },
      { label: 'Health',      to: '/omega/monitor' },
      { label: 'Market Intel', to: '/omega/market-intel' },
      { label: 'Budget',      to: '/omega/budget' },
    ],
  },
  {
    title: 'Client Products',
    items: [
      { label: 'Monitor',           to: '/product/monitor' },
      { label: 'Guardian Runtime',  to: '/product/loop' },
    ],
  },
];

/** Width of the expanded sidebar (desktop), in px. */
export const SIDEBAR_WIDTH = 224;

/** True when the viewport is mobile width (≤767px). */
export function useIsMobile(): boolean {
  const query = '(max-width: 767px)';
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

const KILL_SWITCH_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 8px',
  border: '1px solid #dc2626',
  color: '#dc2626',
  borderRadius: 3,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textDecoration: 'none',
  textTransform: 'uppercase',
  flexShrink: 0,
};

function SidebarHeader() {
  return (
    <div
      style={{
        padding: '12px 12px 8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #f3f4f6',
        gap: 8,
      }}
    >
      <Link
        to="/omega"
        style={{
          fontWeight: 700,
          fontSize: 15,
          color: '#111827',
          textDecoration: 'none',
          letterSpacing: '0.02em',
        }}
      >
        OMEGA
      </Link>
      <Link to="/omega/kill-switch" style={KILL_SWITCH_STYLE} aria-label="Kill switch">
        ⚠ Kill
      </Link>
    </div>
  );
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  return (
    <nav aria-label="OMEGA navigation" style={{ padding: '6px 0', flex: 1, overflowY: 'auto' }}>
      {OMEGA_NAV.map((cat) => (
        <div key={cat.title} style={{ marginBottom: 8 }}>
          <div
            style={{
              padding: '6px 14px',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#9ca3af',
            }}
          >
            {cat.title}
          </div>
          {cat.items.map((item) => {
            const active = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                style={{
                  display: 'block',
                  padding: '6px 14px',
                  fontSize: 13,
                  textDecoration: 'none',
                  color: active ? '#111827' : '#4b5563',
                  background: active ? '#f3f4f6' : 'transparent',
                  borderLeft: active ? '3px solid #111827' : '3px solid transparent',
                  fontWeight: active ? 600 : 400,
                }}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function SidebarFooter({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const active = pathname === '/settings';
  return (
    <div style={{ borderTop: '1px solid #f3f4f6', padding: '6px 0' }}>
      <Link
        to="/settings"
        onClick={onNavigate}
        style={{
          display: 'block',
          padding: '6px 14px',
          fontSize: 13,
          textDecoration: 'none',
          color: active ? '#111827' : '#4b5563',
          background: active ? '#f3f4f6' : 'transparent',
          borderLeft: active ? '3px solid #111827' : '3px solid transparent',
          fontWeight: active ? 600 : 400,
        }}
      >
        Settings
      </Link>
    </div>
  );
}

/**
 * The OMEGA sidebar. Desktop: fixed left column. Mobile: hamburger off-canvas drawer.
 */
export function OmegaSidebar() {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const sidebarContent = (
    <aside
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: SIDEBAR_WIDTH,
        height: '100%',
        background: '#ffffff',
        borderRight: '1px solid #e5e7eb',
        overflowY: 'hidden',
      }}
    >
      <SidebarHeader />
      {isMobile
        ? <NavList onNavigate={() => setOpen(false)} />
        : <NavList />}
      {isMobile
        ? <SidebarFooter onNavigate={() => setOpen(false)} />
        : <SidebarFooter />}
    </aside>
  );

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          aria-label="Open OMEGA menu"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 40,
            width: 40,
            height: 40,
            borderRadius: 8,
            border: '1px solid #e5e7eb',
            background: '#ffffff',
            fontSize: 18,
            cursor: 'pointer',
          }}
        >
          ☰
        </button>
        {open && (
          <>
            <div
              onClick={() => setOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 41 }}
            />
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                width: SIDEBAR_WIDTH,
                zIndex: 42,
              }}
            >
              {sidebarContent}
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: SIDEBAR_WIDTH,
        zIndex: 30,
      }}
    >
      {sidebarContent}
    </div>
  );
}

/**
 * OmegaLayoutShell — pathless React Router layout route.
 *
 * Renders OmegaSidebar (always-visible) + the matched child page via <Outlet />.
 * Used in App.tsx as a wrapper for all /omega/* routes so each page inherits
 * the sidebar without importing it individually.
 *
 * The wrapper div shifts page content to the right of the fixed sidebar.
 */
export function OmegaLayoutShell() {
  const isMobile = useIsMobile();
  return (
    <>
      <OmegaSidebar />
      <div style={{ paddingLeft: isMobile ? 0 : SIDEBAR_WIDTH, minHeight: '100vh' }}>
        <Outlet />
      </div>
    </>
  );
}
